import { useState } from "react";
import { Save, AlertCircle } from "lucide-react";
import { Button } from "../ui";
import Drawer from "../ui/Drawer";
import EntitySelect from "./EntitySelect";
import { useToast } from "../ui/Toast";
import {
  useResourceCreate,
  useResourceUpdate,
  type FieldDef,
  type ResourceConfig,
} from "../../lib/admin/resources";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Rec = Record<string, any>;

const isEmpty = (v: unknown) => v === "" || v === null || v === undefined;

function initialValues(cfg: ResourceConfig, record: Rec | null): Rec {
  const v: Rec = {};
  cfg.fields.forEach((f) => {
    if (record && record[f.name] !== undefined && record[f.name] !== null) v[f.name] = record[f.name];
    else if (f.type === "boolean") v[f.name] = false;
    else if (f.type === "number" || f.type === "reference") v[f.name] = null;
    else v[f.name] = "";
  });
  return v;
}

export default function ResourceForm({
  config,
  record,
  onClose,
}: {
  config: ResourceConfig;
  record: Rec | null;
  onClose: () => void;
}) {
  const editing = !!record?.id;
  const [values, setValues] = useState<Rec>(() => initialValues(config, record));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useResourceCreate(config.endpoint);
  const update = useResourceUpdate(config.endpoint);
  const toast = useToast();
  const pending = create.isPending || update.isPending;

  function set(name: string, value: unknown) {
    setValues((v) => ({ ...v, [name]: value }));
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    config.fields.forEach((f) => {
      if (f.required && !f.readOnly && (values[f.name] === "" || values[f.name] == null))
        e[f.name] = "Required";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function onError(err: any) {
    const data = err?.response?.data;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const fieldErrs: Record<string, string> = {};
      Object.entries(data).forEach(([k, val]) => {
        fieldErrs[k] = Array.isArray(val) ? String(val[0]) : String(val);
      });
      setErrors(fieldErrs);
    } else {
      setErrors({ __all__: "Save failed. Please review the fields and try again." });
    }
    toast.error(`Could not save ${config.singular.toLowerCase()}.`);
  }

  function submit() {
    if (!validate()) return;
    const body: Rec = {};
    const jsonErrors: Record<string, string> = {};
    for (const f of config.fields) {
      if (f.readOnly) continue;
      const isPassword = f.type === "password" || f.name === "password";
      if (isPassword && !values[f.name]) continue; // never send an empty password
      let val = values[f.name];
      if (f.type === "json") {
        if (isEmpty(val)) continue; // leave server default untouched
        if (typeof val === "string") {
          try {
            val = JSON.parse(val);
          } catch {
            jsonErrors[f.name] = "Invalid JSON — check the syntax.";
            continue;
          }
        }
      }
      if ((f.type === "number" || f.type === "reference") && isEmpty(val)) {
        if (editing) body[f.name] = null; // clearing an existing value
        continue; // omit on create (server auto-assigns ext_id / leaves FK null)
      }
      body[f.name] = val;
    }
    if (Object.keys(jsonErrors).length) {
      setErrors((e) => ({ ...e, ...jsonErrors }));
      return;
    }
    const done = () => {
      toast.success(`${config.singular} ${editing ? "updated" : "created"}.`);
      onClose();
    };
    if (editing) {
      update.mutate({ id: record!.id, body }, { onSuccess: done, onError });
    } else {
      create.mutate(body, { onSuccess: done, onError });
    }
  }

  return (
    <Drawer
      onClose={onClose}
      eyebrow={editing ? "EDIT" : "CREATE"}
      title={editing ? `Edit ${config.singular}` : `New ${config.singular}`}
      footer={
        <div className="flex gap-2">
          <Button onClick={submit} disabled={pending} className="flex-1">
            <span className="inline-flex items-center gap-1.5"><Save size={15} /> {pending ? "Saving…" : "Save"}</span>
          </Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <div className="space-y-3 p-4">
        {errors.__all__ && (
          <div className="flex items-center gap-1.5 rounded-lg border border-critical/40 bg-critical/10 px-3 py-2 text-xs text-critical">
            <AlertCircle size={14} /> {errors.__all__}
          </div>
        )}
        {config.fields.map((f) => (
          <Field key={f.name} f={f} record={record} value={values[f.name]} error={errors[f.name]} onChange={(v) => set(f.name, v)} editing={editing} />
        ))}
      </div>
    </Drawer>
  );
}

function Field({
  f, record, value, error, onChange, editing,
}: {
  f: FieldDef; record: Rec | null; value: unknown; error?: string; onChange: (v: unknown) => void; editing: boolean;
}) {
  const base = "w-full rounded-lg border bg-white/5 px-3 py-2 text-sm outline-none focus:border-aurora-cyan/50";
  const border = error ? "border-critical/60" : "border-white/10";
  const ro = !!(f.readOnly && editing);

  return (
    <div>
      <label className="mb-1 flex items-center justify-between text-xs text-slatey">
        <span>{f.label}{f.required && <span className="text-critical"> *</span>}</span>
        {f.help && <span className="text-[10px] text-slate-600">{f.help}</span>}
      </label>
      {f.type === "boolean" ? (
        <button
          type="button"
          onClick={() => !ro && onChange(!value)}
          className={`flex h-6 w-11 items-center rounded-full px-0.5 transition ${value ? "bg-aurora-teal/60" : "bg-white/10"}`}
        >
          <span className={`h-5 w-5 rounded-full bg-white transition ${value ? "translate-x-5" : ""}`} />
        </button>
      ) : f.type === "reference" && f.reference ? (
        <EntitySelect
          reference={f.reference}
          value={(value as number | null) ?? null}
          initialLabel={f.reference.labelFrom && record ? (record[f.reference.labelFrom] as string) : undefined}
          onChange={(id) => onChange(id)}
          error={!!error}
          disabled={ro}
          placeholder={f.placeholder ?? `Select ${f.label.toLowerCase()}…`}
        />
      ) : f.type === "select" ? (
        <select value={String(value ?? "")} disabled={ro} onChange={(e) => onChange(e.target.value)} className={`${base} ${border}`}>
          <option value="">—</option>
          {f.options?.map((o) => <option key={o.value} value={o.value} className="bg-ocean-800">{o.label}</option>)}
        </select>
      ) : f.type === "textarea" || f.type === "json" ? (
        <textarea
          value={typeof value === "object" && value !== null ? JSON.stringify(value, null, 2) : String(value ?? "")}
          readOnly={ro} rows={3} placeholder={f.placeholder}
          onChange={(e) => onChange(e.target.value)} className={`${base} ${border} font-mono text-xs`} />
      ) : (
        <input
          type={f.type === "number" ? "number" : f.type === "password" || f.name === "password" ? "password" : "text"}
          value={value == null ? "" : String(value)} readOnly={ro} step={f.step} placeholder={f.placeholder}
          onChange={(e) =>
            onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)
          }
          className={`${base} ${border}`} />
      )}
      {error && <div className="mt-1 text-[11px] text-critical">{error}</div>}
    </div>
  );
}
